// React-Query hooks for the Email module. Gmail-only, live pass-through —
// the server stores no email content, only EmailLink pointers.
//
// Cache key layout:
//   ["crm", "email-accounts"]                          — connected mailboxes
//   ["crm", "email-threads", { ...filters }]           — Inbox thread list
//   ["crm", "email-thread", accountId, threadId]       — full thread (live Gmail)
//   ["crm", "lead-emails", leadId]                     — emails linked to a lead

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";

const ACCOUNTS_KEY = ["crm", "email-accounts"];
const THREADS_KEY = ["crm", "email-threads"];

export function useEmailAccounts() {
  return useQuery({
    queryKey: ACCOUNTS_KEY,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await crmApi.get("/email-accounts");
      return data?.accounts || [];
    },
  });
}

// The 5 vendor-communication SOP segments used by the AI email assistant.
// Static server-side data — cache aggressively. Shape per segment:
//   { segmentKey, sopNumber, title, oneLineSummary }
export function useEmailAssistSegments() {
  return useQuery({
    queryKey: ["crm", "email-assist-segments"],
    staleTime: Infinity,
    queryFn: async () => {
      const { data } = await crmApi.get("/email-assist/segments");
      return data?.segments || [];
    },
  });
}

const SYNC_SETUP_KEY = ["crm", "email-sync-setup"];

/** Org email sync mode — poll vs Pub/Sub push (for Settings diagnostics). */
export function useEmailSyncSetup() {
  return useQuery({
    queryKey: SYNC_SETUP_KEY,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await crmApi.get("/email-accounts/sync-setup");
      return data?.setup || null;
    },
  });
}

/** Re-register Gmail push watches for this org (org_admin only). */
export function useRenewGmailWatches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await crmApi.post("/email-accounts/renew-watches");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: SYNC_SETUP_KEY });
    },
  });
}

export function useConnectGmail() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await crmApi.get("/auth/google/connect-gmail/start");
      return data;
    },
  });
}

export function useDisconnectEmailAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId) => {
      const { data } = await crmApi.delete(`/email-accounts/${accountId}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useUpdateEmailSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, signatureHtml }) => {
      const { data } = await crmApi.patch(`/email-accounts/${accountId}/signature`, { signatureHtml });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS_KEY }),
  });
}

export function useMailboxStats(accountId) {
  return useQuery({
    enabled: !!accountId,
    queryKey: ["crm", "email-stats", accountId],
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data } = await crmApi.get(`/email-accounts/${accountId}/stats`);
      return data?.stats || null;
    },
  });
}

export function useModifyThread() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, threadId, action }) => {
      const { data } = await crmApi.post(
        `/email-accounts/${accountId}/threads/${threadId}/modify`,
        { action },
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: THREADS_KEY });
      qc.invalidateQueries({ queryKey: ["crm", "email-stats", vars.accountId] });
      if (vars?.accountId && vars?.threadId) {
        qc.invalidateQueries({ queryKey: ["crm", "email-thread", vars.accountId, vars.threadId] });
      }
    },
  });
}

// Inbox list — every call hits Gmail live. Pass accountId to narrow to one
// mailbox, otherwise the server picks the user's default. Uses Gmail's
// opaque pageToken cursor; useInfiniteQuery threads it through automatically.
// `q` is Gmail search syntax. `labelId` scopes to one of Gmail's system
// folders (INBOX/SENT/STARRED/IMPORTANT/SPAM/TRASH); falsy = all mail.
// Caller flattens pages.flatMap(p => p.threads).
export function useEmailThreads({ accountId, q, limit = 30, labelId, refetchInterval } = {}) {
  const url = accountId ? `/email-accounts/${accountId}/threads` : "/email-accounts/threads";
  return useInfiniteQuery({
    queryKey: [...THREADS_KEY, { accountId: accountId || null, q: q || "", limit, labelId: labelId || null }],
    staleTime: 15_000,
    refetchInterval: refetchInterval || false,
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage?.nextPageToken || undefined,
    queryFn: async ({ pageParam }) => {
      const { data } = await crmApi.get(url, {
        params: {
          q: q || undefined,
          limit,
          pageToken: pageParam || undefined,
          labelId: labelId || undefined,
        },
      });
      return data;
    },
  });
}

export function useEmailThread(accountId, threadId) {
  return useQuery({
    enabled: !!accountId && !!threadId,
    queryKey: ["crm", "email-thread", accountId, threadId],
    staleTime: 15_000,
    queryFn: async () => {
      // A single thread is fetched live from Gmail in full format (every message
      // body + inline images + attachment metadata). Long threads routinely take
      // well over the default 15s crmApi timeout — bump it to match the other
      // live-Gmail reads (lead emails use 45s, sync uses 120s).
      const { data } = await crmApi.get(`/email-accounts/${accountId}/threads/${threadId}`, {
        timeout: 60000,
      });
      return data;
    },
  });
}

export function useLeadEmails(leadId) {
  return useQuery({
    enabled: !!leadId,
    queryKey: ["crm", "lead-emails", leadId],
    staleTime: 30_000,
    queryFn: async () => {
      // Live Gmail fetches can be slow — override the default 15s client timeout.
      const { data } = await crmApi.get(`/leads/${leadId}/emails`, { timeout: 45000 });
      return data?.messages || [];
    },
  });
}

// Read a browser File into a base64 string (no data: prefix). Backend will
// re-wrap into MIME. We do this in the browser so the server payload is
// already-encoded — no multipart parsing on the server side.
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || "";
      const comma = String(result).indexOf(",");
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

// Send / reply. For replies, pass `replyTo` carrying the thread-pivot bits
// from the message you're responding to: { providerMessageId, providerThreadId,
// inReplyTo, references, subject }. Server has no DB to look these up.
//
// `attachments` (optional): array of browser File objects. We convert them to
// base64 client-side and POST them inside the JSON body (matches the 22 MB
// limit the backend enforces).
export function useSendEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, to, cc, bcc, subject, text, html, replyTo, leadId, attachments }) => {
      let attPayload;
      if (Array.isArray(attachments) && attachments.length) {
        attPayload = await Promise.all(attachments.map(async (f) => ({
          filename: f.name,
          mimeType: f.type || "application/octet-stream",
          contentBase64: await fileToBase64(f),
        })));
      }
      const { data } = await crmApi.post(`/email-accounts/${accountId}/send`, {
        to, cc, bcc, subject, text, html, replyTo, leadId,
        attachments: attPayload,
      });
      return data;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: THREADS_KEY });
      if (vars?.leadId) {
        qc.invalidateQueries({ queryKey: ["crm", "lead-emails", vars.leadId] });
        // EMAIL_SENT / EMAIL_REPLIED activity row is emitted server-side when
        // a message is attached to a lead — refresh the per-lead timeline so
        // it shows up without a manual reload.
        qc.invalidateQueries({ queryKey: ["crm", "activities", vars.leadId] });
      }
      // Cross-lead activity feed (dashboard "Recent activity", Activities tab)
      // can also include EMAIL_SENT rows, refresh too.
      qc.invalidateQueries({ queryKey: ["crm", "activities", "feed"] });
      qc.invalidateQueries({ queryKey: ["crm", "dashboard", "overview"] });
      if (data?.threadId) {
        qc.invalidateQueries({ queryKey: ["crm", "email-thread", vars.accountId, data.threadId] });
      }
    },
  });
}

export function useScheduleEmail() {
  return useMutation({
    mutationFn: async ({ accountId, to, cc, bcc, subject, text, html, replyTo, leadId, attachments, scheduledAt }) => {
      let attPayload;
      if (Array.isArray(attachments) && attachments.length) {
        attPayload = await Promise.all(attachments.map(async (f) => ({
          filename: f.name,
          mimeType: f.type || "application/octet-stream",
          contentBase64: await fileToBase64(f),
        })));
      }
      const { data } = await crmApi.post(`/email-accounts/${accountId}/schedule`, {
        to, cc, bcc, subject, text, html, replyTo, leadId,
        attachments: attPayload,
        scheduledAt,
      });
      return data;
    },
  });
}

// Download an attachment as a Blob (auth header is set by the axios
// interceptor) and trigger a browser save. Returns a Promise that resolves
// once the click() has been dispatched.
export async function downloadAttachment({ accountId, messageId, attachmentId, filename, mimeType }) {
  const res = await crmApi.get(
    `/email-accounts/${accountId}/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
    {
      responseType: "blob",
      params: { filename, mimeType },
    },
  );
  const blob = res.data;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "attachment";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a beat to start the download before we revoke.
  setTimeout(() => URL.revokeObjectURL(url), 500);
}

export function useMarkThreadRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, threadId, isRead = true }) => {
      const { data } = await crmApi.post(
        `/email-accounts/${accountId}/threads/${threadId}/read`,
        { isRead }
      );
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: THREADS_KEY });
      qc.invalidateQueries({ queryKey: ["crm", "email-thread", vars.accountId, vars.threadId] });
    },
  });
}

export function useManualSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (accountId) => {
      const { data } = await crmApi.post(`/email-accounts/${accountId}/sync`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: THREADS_KEY }),
  });
}

// Toggle Gmail STARRED on a thread — the inbox "bookmark" button. Body
// shape mirrors useMarkThreadRead. We optimistically patch the cached
// threads list so the star fills/empties instantly; the refetch reconciles.
export function useMarkThreadStarred() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountId, threadId, isStarred = true }) => {
      const { data } = await crmApi.post(
        `/email-accounts/${accountId}/threads/${threadId}/star`,
        { isStarred }
      );
      return data;
    },
    onMutate: async ({ accountId, threadId, isStarred = true }) => {
      // Touch every thread-list cache shape — infinite query with multiple
      // filter combos. We patch in place so the user sees the star flip
      // before the server roundtrip lands.
      await qc.cancelQueries({ queryKey: THREADS_KEY });
      const snapshots = qc.getQueriesData({ queryKey: THREADS_KEY });
      for (const [key, value] of snapshots) {
        if (!value || !Array.isArray(value.pages)) continue;
        qc.setQueryData(key, (prev) => {
          if (!prev?.pages) return prev;
          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              threads: (page.threads || []).map((t) => {
                if (String(t.id || t.threadId) !== String(threadId)) return t;
                const labels = new Set(t.labels || []);
                if (isStarred) labels.add("STARRED"); else labels.delete("STARRED");
                return { ...t, isStarred, labels: [...labels] };
              }),
            })),
          };
        });
      }
      return { snapshots };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back every snapshotted query if the server says no.
      for (const [key, value] of ctx?.snapshots || []) {
        qc.setQueryData(key, value);
      }
    },
    onSettled: (_d, _err, vars) => {
      qc.invalidateQueries({ queryKey: THREADS_KEY });
      if (vars?.accountId && vars?.threadId) {
        qc.invalidateQueries({ queryKey: ["crm", "email-thread", vars.accountId, vars.threadId] });
      }
    },
  });
}

// Backfill Gmail history for a lead — used by the "Sync" button on the
// drawer's Email tab. Returns { ingested, accountsScanned, reason? }. We
// invalidate the per-lead emails list + the cross-lead activity feed so
// any new EMAIL_RECEIVED rows surface immediately.
export function useSyncLeadEmails() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadId) => {
      // Sync can scan up to ~200 messages × N mailboxes (sequential Gmail
      // metadata fetches) — well over the default 15s crmApi timeout.
      const { data } = await crmApi.post(`/leads/${leadId}/emails/sync`, null, {
        timeout: 120000,
      });
      return data;
    },
    onSuccess: (_d, leadId) => {
      qc.invalidateQueries({ queryKey: ["crm", "lead-emails", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", leadId] });
      qc.invalidateQueries({ queryKey: ["crm", "activities", "feed"] });
      qc.invalidateQueries({ queryKey: ["crm", "followups", "obligations"] });
      qc.invalidateQueries({ queryKey: ["crm", "leads"] });
    },
  });
}
